import React from 'react';
import { MdContentCopy, MdDelete } from 'react-icons/md';
import { IconButton } from '@aibuild/ui';
import { useEditorContext } from '../useEditorContext';
import { FloatingCluster } from './NodeFloatingActions.styled';

export interface NodeFloatingActionsProps {
  /** ID of the node these actions act on. */
  readonly nodeId: string;
}

/**
 * Naked-icon cluster floating ABOVE the card's top-right corner. The
 * shell shows it only when the card is in
 * {@code NODE_CARD_STATE_SELECTED} (see
 * {@code NodeCardShell.styled.ts}'s {@code FloatingActions} rule), so
 * this component doesn't gate on selection itself — it just renders
 * the buttons and lets the shell's CSS decide visibility.
 *
 * <p>Mirrors the design's `.ns-node__hover-actions` cluster: small,
 * naked icons (no surface), used for "I have committed to this node,
 * give me the destructive affordances now". Hover-only header
 * actions (Run, 3-dot menu) live separately inside the header.</p>
 *
 * <p>Info is intentionally deferred — we don't have a node inspector
 * to open yet. When the inspector lands, add it as the first button
 * in this cluster (matches the design's left-to-right order
 * info → duplicate → delete).</p>
 */
const NodeFloatingActions: React.FC<NodeFloatingActionsProps> = ({
  nodeId,
}) => {
  const { duplicateNode, removeNode, readOnly } = useEditorContext();
  if (readOnly) return null;
  return (
    <FloatingCluster>
      <IconButton
        ariaLabel="Duplicate node"
        tooltip="Duplicate"
        icon={<MdContentCopy />}
        size="sm"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          duplicateNode(nodeId);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        dataTestId={`process-node__floating-duplicate-${nodeId}`}
      />
      <IconButton
        ariaLabel="Delete node"
        tooltip="Delete"
        icon={<MdDelete />}
        size="sm"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          removeNode(nodeId);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        dataTestId={`process-node__floating-delete-${nodeId}`}
      />
    </FloatingCluster>
  );
};

export default NodeFloatingActions;
